import Env from '@/plugins/protokol/lib/Env'

/**
 * Streams component models from the API via NDJSON fetch.
 * Axios does not support ReadableStream, so we use fetch directly.
 *
 * Response format (newline-delimited JSON):
 *   Line 1: { total, total_filtered, total_pages, page }  → type: 'metadata'
 *   Line 2..N: individual record objects                   → type: 'record'
 *   Line N+1 (optional): { next_cursor }                  → type: 'cursor'
 *
 * @param {string} componentRef - component UUID (or UUID.schema)
 * @param {Object} params - query params (offset, limit, cursor, etc.)
 * @param {AbortSignal} abortSignal
 * @param {Function} onEvent - called with { type, data } for each parsed line
 * @returns {Promise}
 */
export function streamModels(componentRef, params, abortSignal, onEvent) {
  var apiHost = Env.get('API_HOST')
  var projectUUID = localStorage.getItem('current_project_uuid') || ''
  var workspaceUUID = localStorage.getItem('current_workspace_uuid') || ''
  var projectEnv = localStorage.getItem('current_env') || 'live'
  var locale = localStorage.getItem('locale') || 'en_US'

  var headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/x-ndjson',
    'Accept-Language': locale,
    'X-Project-Uuid': projectUUID,
    'X-Workspace-Uuid': workspaceUUID,
    'X-Project-Env': projectEnv,
  }

  return fetch(
    apiHost + '/v4/system/component/' + componentRef + '/models/stream',
    {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify(params),
      signal: abortSignal
    }
  ).then(function (response) {
    if (!response.ok) {
      var status = response.status
      if (status === 401) {
        window.location = '/login'
        return
      }
      return response.json().then(function (data) {
        var msg = (data && (data.error || data.message)) || ('Request failed with status ' + status)
        var err = new Error(msg)
        err.status = status
        throw err
      }).catch(function (e) {
        if (e.status) throw e
        throw new Error('Request failed with status ' + status)
      })
    }

    var reader = response.body.getReader()
    var decoder = new TextDecoder()
    var buffer = ''
    var isFirst = true

    function processLine(line) {
      line = line.trim()
      if (!line) return
      try {
        var parsed = JSON.parse(line)
        if (isFirst) {
          onEvent({ type: 'metadata', data: parsed })
          isFirst = false
        } else if (parsed.next_cursor !== undefined && Object.keys(parsed).length === 1) {
          onEvent({ type: 'cursor', data: parsed })
        } else {
          onEvent({ type: 'record', data: parsed })
        }
      } catch (e) {
        // ignore malformed lines
      }
    }

    function read() {
      return reader.read().then(function (result) {
        if (result.done) {
          if (buffer.trim()) processLine(buffer)
          onEvent({ type: 'done' })
          return
        }

        buffer += decoder.decode(result.value, { stream: true })
        var lines = buffer.split('\n')
        buffer = lines.pop()

        for (var i = 0; i < lines.length; i++) {
          processLine(lines[i])
        }

        return read()
      })
    }

    return read()
  })
}
